/**
 * Design Tokens
 * 
 * Based on the "perfect" Salary Management style.
 * These variables serve as the single source of truth for the entire app.
 */

export const designTokens = {
    colors: {
        primary: "#1890ff", // Reverted to classic AntD Blue
        secondary: "#722ed1", // Reverted to classic AntD Purple
        success: "#52c41a", // Emerald-500
        warning: "#faad14", // Amber-500
        error: "#f5222d", // Rose-500

        background: {
            light: "#F1F5F9", // Slate-100 (airy background)
            dark: "#000000",   // True black as seen in Salary Management
        },

        card: {
            light: "#FFFFFF",
            dark: "#000000", // Cards in salary management blend with black or have subtle borders
        },

        text: {
            light: "#0f172a", // Slate-900
            dark: "#f1f5f9",  // Slate-100
            secondary: "#64748b", // Slate-500
        },

        border: {
            light: "#e2e8f0", // Slate-200
            dark: "#1e293b",  // Slate-800
        }
    },

    typography: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",

        // ── Fixed numeric values ─────────────────────────────────────────────
        // Used by Ant Design ConfigProvider (parseInt-safe, numbers only).
        // These are the mid-point "comfortable" values for the fluid scale.
        fontSizeBase: 14, // Ant Design base — do NOT change to clamp()
        fontSize: {
            xs: "11px",    // Ant Design: badges, column headers
            sm: "12px",    // Ant Design: captions, secondary labels
            base: "14px",  // Ant Design: body, table cells, form inputs
            md: "15px",    // Ant Design: elevated cells, task titles
            lg: "16px",    // Ant Design: card headers, drawer subheadings
            xl: "18px",    // Ant Design: modal headers, section banners
            "2xl": "22px", // Ant Design: page titles
            "3xl": "26px", // Ant Design: KPI counters, hero stats
        },

        // ── Fluid clamp() values ─────────────────────────────────────────────
        // Used exclusively by CSS custom properties in app.less.
        // These cascade to all 621 frontend files via the CSS variable layer.
        // clamp(min, preferred-fluid, max)
        //   min  = smallest readable size (mobile ~360px)
        //   preferred = viewport-relative calc that scales smoothly
        //   max  = cap for large/ultrawide monitors (1920px+)
        fontSizeFluid: {
            xs: "clamp(10px, 0.7vw + 8px,  11px)", // badges, timestamps, pills
            sm: "clamp(11px, 0.8vw + 9px,  12px)", // captions, breadcrumbs
            base: "clamp(13px, 0.9vw + 10px, 14px)", // body text, inputs, table cells
            md: "clamp(14px, 1vw  + 11px,  15px)", // elevated cells, list items
            lg: "clamp(15px, 1.1vw + 11px, 16px)", // card titles, subheadings
            xl: "clamp(16px, 1.3vw + 12px, 18px)", // modal headers, section titles
            "2xl": "clamp(18px, 1.8vw + 14px, 22px)", // page titles
            "3xl": "clamp(20px, 2.2vw + 16px, 26px)", // KPI numbers, hero stats
        },

        fontWeight: {
            normal: "400",
            medium: "500",
            semibold: "600",
            bold: "700",
            black: "800",
        }
    },

    spacing: {
        radius: "12px", // Consistent rounded corners
    },

    shadows: {
        sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        base: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    }
};
